import { Injectable, NgZone } from '@angular/core';
import _Pusher from 'pusher-js';

import { defaultHeaders } from '../lib/server-http';
import { Session } from './session';
import { ServerApi } from './server-api';
import { ChannelWrapper } from './channel-wrapper';
import { NetworkStateService, NetworkStates } from '../services/network-state.service';
import { logd } from '../lib/utils';
import { getApiUrl } from '@lib/environments.const';
import { BulkPusherChannelWrapper } from '@classes/bulk-pusher-channel-wrapper';

const API_URL: string = getApiUrl();

@Injectable({
  providedIn: 'root',
})
export class Pusher {
  private userId;
  private _pusher;

  constructor(
    private zone: NgZone,
    private session: Session,
    private api: ServerApi,
    private networkStateService: NetworkStateService,
  ) {
    this.userId = session.getUserId();

    const options = {
      encrypted: true,
      authEndpoint: `${API_URL}/api/w2/callbacks/pusher`,
      auth: { headers: defaultHeaders },
    };
    this._pusher = new _Pusher(PUSHER_KEY, options);
    this.bindEvents();
    _Pusher.logToConsole = true;
    _Pusher.log = function (message) {
      logd(message);
    };
  }

  public getUserChannel(
    name,
    enablePusherInHijack: boolean = false,
    channelNamePrefix = 'private-user',
    id = this.userId,
    joinData = {},
    endPointPath = '',
  ) {
    return new ChannelWrapper(
      this._pusher,
      this.api,
      this.zone,
      channelNamePrefix,
      id,
      name,
      joinData,
      endPointPath,
      enablePusherInHijack,
    );
  }

  public getBulkChannel<TId, TJoinData, TJoinResponse, TEventData>(config: {
    channelNamePrefix: string;
    channelNameSuffix: string;
    ids: TId[];
    joinData: TJoinData;
    endPointPath?: string;
    enablePusherInHijack?: boolean;
    pusherKeepAliveInterval?: number;
  }): BulkPusherChannelWrapper<TId, TJoinData, TJoinResponse, TEventData> {
    return new BulkPusherChannelWrapper<TId, TJoinData, TJoinResponse, TEventData>({
      pusher: this._pusher,
      api: this.api,
      zone: this.zone,
      channelNamePrefix: config.channelNamePrefix,
      channelNameSuffix: config.channelNameSuffix,
      ids: config.ids,
      joinData: config.joinData,
      endPointPathPostfix: config.endPointPath,
      enablePusherInHijack: config.enablePusherInHijack || false,
      pusherKeepAliveInterval: config.pusherKeepAliveInterval,
    });
  }

  private bindEvents(): void {
    const pusher = this._pusher;
    pusher.connection.bind('connected', () => {
      this.networkStateService.stateSubject.next(NetworkStates.CONNECTED);
    });
    pusher.connection.bind('connecting', () => {
      this.networkStateService.stateSubject.next(NetworkStates.CONNECTING);
    });
    pusher.connection.bind('unavailable', () => {
      this.networkStateService.stateSubject.next(NetworkStates.DISCONNECTED);
    });
  }
}
